import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { ItemThumb } from '@/components/ui/ItemThumb';
import { getDo, type DispatchOrder } from '@/api/dispatchOrder';
import { useDocViewer } from '@/components/ui/useDocViewer';
import { formatDate, formatINRSymbol } from '@/lib/format';
import { orderRef } from '@/lib/orderRef';

interface Props {
  open: boolean;
  doId: number | null;
  onClose: () => void;
}

const STAGE_TONE: Record<string, 'muted' | 'accent' | 'warning' | 'success' | 'danger' | 'info'> = {
  active: 'accent',
  'credit-pending': 'warning',
  'credit-approved': 'success',
  'credit-rejected': 'danger',
  fulfilled: 'success',
  cancelled: 'muted',
};
const CREDIT_TONE = { GREEN: 'success', AMBER: 'warning', RED: 'danger' } as const;

export function DoDetailModal({ open, doId, onClose }: Props) {
  const query = useQuery({
    queryKey: ['do-detail', doId],
    queryFn: () => getDo(doId as number),
    enabled: open && !!doId,
  });
  const d = query.data as DispatchOrder | undefined;
  const docViewer = useDocViewer();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={d ? `Dispatch Order — ${d.doNo}` : 'Dispatch Order'}
      size="xl"
      footer={
        <>
          {d && (
            <Button
              variant="primary"
              onClick={() => docViewer.open('do-sales-order', d.id, d.doNo)}
            >
              Sales Order (this batch)
            </Button>
          )}
          <Button variant="glass" onClick={onClose}>Close</Button>
        </>
      }
    >
      {query.isFetching && !d && (
        <div className="p-10 text-center text-sm text-[var(--text-muted)]">Loading…</div>
      )}
      {!d ? null : (
        <div className="space-y-4">
          {/* Header meta */}
          <div className="grid grid-cols-4 gap-3 text-sm">
            <Field label="DO Date">{formatDate(d.doDate)}</Field>
            <Field label="Order Ref">{orderRef(d.enquiry)}</Field>
            <Field label="Expected Dispatch">{formatDate(d.expectedDispatch)}</Field>
            <Field label="Sales Person">{d.salesPerson ?? '—'}</Field>
            <Field label="Stage">
              <Badge tone={STAGE_TONE[d.stage] ?? 'muted'}>{d.stage}</Badge>
            </Field>
            <Field label="Status">
              <span className="font-semibold">{d.status}</span>
            </Field>
            <Field label="Credit">
              {d.creditStatus ? (
                <Badge tone={CREDIT_TONE[d.creditStatus]}>{d.creditStatus}</Badge>
              ) : (
                <span className="text-[var(--text-muted)]">—</span>
              )}
            </Field>
            <Field label="Partial?">{d.isPartial ? 'Yes' : 'No'}</Field>
          </div>

          {/* Customer / delivery */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-card border border-[var(--border-color)] bg-white/[0.02] p-3 text-xs">
              <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
                Customer
              </div>
              <div className="text-sm font-bold">{d.party.name}</div>
              {d.party.customerType && (
                <div className="mt-0.5 text-[11px] text-[var(--text-secondary)]">
                  {d.party.customerType}
                </div>
              )}
            </div>
            <div className="rounded-card border border-[var(--border-color)] bg-white/[0.02] p-3 text-xs">
              <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
                Delivery Address
              </div>
              <div className="whitespace-pre-line text-[12px] text-[var(--text-secondary)]">
                {d.deliveryAddress || '—'}
              </div>
            </div>
          </div>

          {/* Enquiry note + Credit note — side by side on one line */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-card border border-info/30 bg-info/[0.06] p-3 text-xs">
              <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
                Enquiry Note{d.enquiry?.enqNo ? ` · ${orderRef(d.enquiry)}` : ''}
              </div>
              <div className="text-[12px] text-[var(--text-secondary)]">
                {d.enquiry?.remarks || <span className="italic text-[var(--text-muted)]">No note added on the enquiry</span>}
              </div>
            </div>
            <div
              className={clsx(
                'rounded-card border p-3 text-xs',
                d.creditStatus === 'RED'
                  ? 'border-danger/40 bg-danger/[0.08]'
                  : d.creditStatus === 'AMBER'
                  ? 'border-warning/40 bg-warning/[0.08]'
                  : 'border-[var(--border-color)] bg-white/[0.02]',
              )}
            >
              <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
                Credit Note
              </div>
              <div className="text-[12px]">{d.creditNote || <span className="italic text-[var(--text-muted)]">—</span>}</div>
            </div>
          </div>

          {/* Remarks — shown only when the DO carries its own remarks */}
          {d.remarks && (
            <div className="rounded-card border border-[var(--border-color)] bg-white/[0.02] p-3 text-xs">
              <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
                Remarks
              </div>
              <div className="text-[12px] italic text-[var(--text-secondary)]">"{d.remarks}"</div>
            </div>
          )}

          {/* Items */}
          <div>
            <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
              Items ({d.items.length})
            </div>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-accent/[0.05]">
                  <th className="p-2 text-left text-[10px] uppercase text-[var(--text-muted)]">Item</th>
                  <th className="p-2 text-left text-[10px] uppercase text-[var(--text-muted)]">SKU Barcode</th>
                  <th className="p-2 text-left text-[10px] uppercase text-[var(--text-muted)]">HSN</th>
                  <th className="p-2 text-right text-[10px] uppercase text-[var(--text-muted)]">Qty</th>
                  <th className="p-2 text-left text-[10px] uppercase text-[var(--text-muted)]">Unit</th>
                  <th className="p-2 text-right text-[10px] uppercase text-[var(--text-muted)]">Rate</th>
                  <th className="p-2 text-right text-[10px] uppercase text-[var(--text-muted)]">GST%</th>
                  <th className="p-2 text-right text-[10px] uppercase text-[var(--text-muted)]">Amount</th>
                </tr>
              </thead>
              <tbody>
                {d.items.map((it) => (
                  <tr key={it.id} className="border-b border-white/[0.04]">
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        <ItemThumb src={it.imageUrl} />
                        <div>
                          <div className="font-semibold">{it.itemName}</div>
                          {it.itemCode && (
                            <div className="text-[10px] text-[var(--text-muted)]">{it.itemCode}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-2 text-[11px] font-mono text-[var(--text-secondary)]">{it.barcode ?? '—'}</td>
                    <td className="p-2 text-[11px] text-[var(--text-muted)]">{it.hsn ?? '—'}</td>
                    <td className="p-2 text-right">{it.qty}</td>
                    <td className="p-2">{it.unit}</td>
                    <td className="p-2 text-right">{formatINRSymbol(it.rate)}</td>
                    <td className="p-2 text-right">{it.gstPct}%</td>
                    <td className="p-2 text-right font-bold text-accent">{formatINRSymbol(it.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="p-2 text-right text-[11px] font-bold uppercase text-[var(--text-muted)]" colSpan={7}>
                    Total Value
                  </td>
                  <td className="p-2 text-right text-base font-black text-accent">
                    {formatINRSymbol(d.totalValue)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Pick lists */}
          {d.pickLists && d.pickLists.length > 0 && (
            <div>
              <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
                Pick Lists ({d.pickLists.length})
              </div>
              <div className="flex flex-wrap gap-2">
                {d.pickLists.map((p) => (
                  <span
                    key={p.id}
                    className="rounded-full border border-[var(--border-color)] bg-white/[0.03] px-2.5 py-0.5 text-[11px]"
                  >
                    <strong className="text-accent">{p.pickNo}</strong>{' '}
                    <span className="text-[var(--text-muted)]">· {p.status}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
        {label}
      </div>
      <div className="mt-0.5 text-sm font-bold text-[var(--text-primary)]">{children}</div>
    </div>
  );
}
